import * as amplitude from '@amplitude/analytics-browser';
import { type ValidPropertyType } from '@amplitude/analytics-types';
import Cookies from 'js-cookie';

import Config from 'Config';
import { isMobile } from 'helpers/device';
import { sharedStore } from 'stores/shared-store';
import { isEmailValid } from 'helpers/validators/email';

export enum AmplitudeUserProperty {
  SourceName = 'Source Name',
  AccountsAB = 'accounts ab',
}

export enum AmplitudeEventProperty {
  SourceName = 'Source Name',
}
const SOURCE_NAME = 'TextAccounts:Web';
const DEFAULT_USER_ID = 'unknown user';

class Amplitude {
  sourceNamePlugin(): amplitude.Types.EnrichmentPlugin {
    return {
      execute: async (event: amplitude.Types.Event) => ({
        ...event,
        event_properties: {
          ...event.event_properties,
          [AmplitudeEventProperty.SourceName]: SOURCE_NAME,
        },
      }),
    };
  }

  setUserProperties(properties: Partial<Record<AmplitudeUserProperty, ValidPropertyType>>): void {
    const identify = new amplitude.Identify();
    Object.entries(properties).forEach(([key, value]) => {
      identify.set(key, value);
    });
    amplitude.identify(identify);
  }

  logEvent(login: string | null, eventName: string, eventProperties = {}, flush = false): void {
    let userID: string | null = login || (sharedStore.get('email') as string);

    if (!userID || !isEmailValid(userID)) {
      const userIdFromCookie = amplitude.getUserId();
      userID = isEmailValid(userIdFromCookie) ? userIdFromCookie : DEFAULT_USER_ID;
    }

    if (userID) {
      amplitude.setUserId(userID);
    }

    const options = {
      ...eventProperties,
      deviceType: isMobile() ? 'mobile' : 'desktop',
      user_id: userID,
    };

    amplitude.logEvent(eventName, options);

    if (flush) {
      amplitude.flush();
    }

    if (!Config.isProduction()) {
      console.log(
        '[APP_EVENT]',
        'Login',
        login,
        'Event:',
        eventName,
        'Properties',
        JSON.stringify(options)
      );
    }
  }

  init(): void {
    if (import.meta.env.VITE_APP_AMPLITUDE_API_KEY && import.meta.env.VITE_APP_AMPLITUDE_API_URL) {
      const logLevel = Config.isDev()
        ? amplitude.Types.LogLevel.Warn
        : amplitude.Types.LogLevel.Error;

      amplitude.init(import.meta.env.VITE_APP_AMPLITUDE_API_KEY, undefined, {
        flushQueueSize: 20,
        flushIntervalMillis: 10000,
        useBatch: true,
        serverUrl: import.meta.env.VITE_APP_AMPLITUDE_API_URL,
        logLevel,
        defaultTracking: {
          pageViews: false,
          formInteractions: false,
        },
      });
      amplitude.add(this.sourceNamePlugin());
      this.setUserProperties({
        [AmplitudeUserProperty.SourceName]: SOURCE_NAME,
        [AmplitudeUserProperty.AccountsAB]: Cookies.get('rc'),
      });
    }
  }
}

export default new Amplitude();
